import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const AssetItemContext = createContext();

export const AssetItemProvider = ({ children }) => {
  const [assetItems, setAssetItems] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    limit: 10,
    assetGroupCode: "",
    assetUsage: "",
    assetStatus: "",
  });

  const baseQueryParams = {
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: filterOptions.limit,
    include: "assetGroup,atvStatus",
  };

  const queryString = buildQueryString({
    ...baseQueryParams,
    "filter[asset_group_code]": filterOptions.assetGroupCode || "",
    "filter[asset_usage]": filterOptions.assetUsage || "",
    "filter[status]": filterOptions.assetStatus || "",
  });

  const fallbackQueryString = buildQueryString(baseQueryParams);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchAssetItems = useCallback(async () => {
    setAssetItems((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      let response = await getRequest(`/ahr/asset-items${queryString}`);
      if (!response.success && response.message?.includes("Requested filter")) {
        response = await getRequest(`/ahr/asset-items${fallbackQueryString}`);
      }
      if (response.success) {
        setAssetItems({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page || 1,
        }));
      }
    } catch (error) {
      setAssetItems({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [fallbackQueryString, queryString]);

  useEffect(() => {
    fetchAssetItems();
  }, [fetchAssetItems]);

  const handleLimitChange = (newLimit) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      limit: newLimit,
      currentPage: 1, // Reset to first page when limit changes
    }));
  };

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const handleAssetFiltersChange = useCallback((nextFilters) => {
    setFilterOptions((prevState) => {
      const isSame = Object.keys(nextFilters).every(
        (key) => prevState[key] === nextFilters[key]
      );
      if (isSame) return prevState;

      return {
        ...prevState,
        ...nextFilters,
        currentPage: 1,
      };
    });
  }, []);

  const totalPages = assetItems.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
    limit: filterOptions.limit,
    onLimitChange: handleLimitChange,
  });

  const Pagination = () => PaginationComponent;

  return (
    <AssetItemContext.Provider
      value={{
        assetItems,
        filterOptions,
        handleSearch,
        handleAssetFiltersChange,
        refetch: fetchAssetItems,
        Pagination,
      }}
    >
      {children}
    </AssetItemContext.Provider>
  );
};

export const useAssetItem = () => {
  const context = useContext(AssetItemContext);
  if (!context) {
    throw new Error("useAssetItem must be used within AssetItemProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/ahr/asset-items?search=${inputValue}`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
